import { useEffect } from 'react';

export const useScrollSnap = (isLoading) => {
  useEffect(() => {
    if (isLoading) return;

    let isScrolling = false;
    let currentSectionIndex = 0;

    const sections = document.querySelectorAll('[data-scroll-section]');

    if (sections.length === 0) return;

    const getSectionPositions = () => {
      return Array.from(sections).map(section => {
        const rect = section.getBoundingClientRect();
        return rect.top + window.pageYOffset;
      });
    };

    const getCurrentSection = () => {
      const scrollY = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const positions = getSectionPositions();

      for (let i = 0; i < positions.length; i++) {
        const sectionTop = positions[i];
        const sectionBottom = i < positions.length - 1 ? positions[i + 1] : document.documentElement.scrollHeight;

        if (scrollY >= sectionTop - windowHeight / 2 && scrollY < sectionBottom - windowHeight / 2) {
          return i;
        }
      }
      return 0;
    };

    const scrollToSection = (index) => {
      if (isScrolling || index < 0 || index >= sections.length) return;

      isScrolling = true;
      currentSectionIndex = index;

      const positions = getSectionPositions();
      const startTop = window.pageYOffset;
      const targetTop = positions[index];
      const distance = targetTop - startTop;
      const duration = 1000;
      let startTime = null;

      const easeInOutCubic = (t) => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const animateScroll = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);

        window.scrollTo(0, startTop + distance * easedProgress);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          isScrolling = false;
        }
      };

      requestAnimationFrame(animateScroll);
    };

    const handleWheel = (e) => {
      if (isScrolling) {
        e.preventDefault();
        return;
      }

      const currentSection = sections[currentSectionIndex];
      if (currentSection && currentSection.classList.contains('gallery-section')) {
        const rect = currentSection.getBoundingClientRect();
        const scrollY = window.pageYOffset;
        const sectionTop = rect.top + scrollY;
        const distanceFromTop = scrollY - sectionTop;

        if (distanceFromTop > 200 && e.deltaY > 0) {
          return;
        }

        if (distanceFromTop <= 200 && e.deltaY < 0 && currentSectionIndex > 0) {
          e.preventDefault();
          scrollToSection(currentSectionIndex - 1);
          return;
        }
      }

      const isLastSection = currentSectionIndex === sections.length - 1;
      const lastSection = sections[isLastSection ? currentSectionIndex : sections.length - 1];
      if (lastSection) {
        const rect = lastSection.getBoundingClientRect();
        const scrollY = window.pageYOffset;
        const sectionBottom = rect.bottom + scrollY;
        const windowBottom = scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;

        if (isLastSection && e.deltaY > 0 && windowBottom >= sectionBottom - 100) {
          if (documentHeight > sectionBottom) {
            return;
          }
        }
      }

      e.preventDefault();

      const direction = e.deltaY > 0 ? 1 : -1;
      const newIndex = currentSectionIndex + direction;

      if (newIndex >= 0 && newIndex < sections.length) {
        scrollToSection(newIndex);
      }
    };

    const handleScroll = () => {
      if (!isScrolling) {
        currentSectionIndex = getCurrentSection();
      }
    };

    const initTimeout = setTimeout(() => {
      currentSectionIndex = getCurrentSection();
      scrollToSection(currentSectionIndex);
    }, 500);

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('scroll', handleScroll, { passive: true });

    const handleAnchorClick = (e) => {
      const href = e.target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          const targetRect = target.getBoundingClientRect();
          const targetTop = targetRect.top + window.pageYOffset;
          window.scrollTo({
            top: targetTop,
            behavior: 'smooth'
          });
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);

    return () => {
      clearTimeout(initTimeout);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleAnchorClick);
    };
  }, [isLoading]);
};

